import React, { useState, useEffect, useCallback } from 'react';
import './index.css';

// --- Configuration ---
// TODO: Replace with your actual Lambda Function URL
const lambdaUrl = 'https://dqfbc3myqi74ocsafrxyiexy7e0gyqyy.lambda-url.us-east-2.on.aws/';

// Must match INSTANCE_KEY in lambda.py
const INSTANCE_KEY = 'converge_ai';

function App() {
  const [isRunning, setIsRunning] = useState(false);
  const [instanceName, setInstanceName] = useState('Loading...');
  const [clusterName, setClusterName] = useState('Loading...');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [activity, setActivity] = useState([]);

  // --- Helper Functions ---

  const showNotification = (message, type = 'processing') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const addActivity = (icon, text) => {
    const newItem = { icon, text, time: new Date().toLocaleTimeString() };
    setActivity(prev => [newItem, ...prev].slice(0, 10));
  };

  // --- API Functions ---

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await fetch(lambdaUrl, {
        method: 'GET',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      const data = await resp.json();
      setIsRunning(!!data[INSTANCE_KEY]);
      if (data.environmentName) setInstanceName(data.environmentName);
      if (data.clusterName) setClusterName(data.clusterName);
    } catch (e) {
      console.error('Failed to fetch status:', e);
    }
  }, []);

  const handleToggle = async (newCheckedState) => {
    const action = newCheckedState ? 'start' : 'stop';

    showNotification(`${action === 'start' ? 'Starting' : 'Stopping'} ${instanceName}...`, 'processing');
    setIsLoading(true);

    try {
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instance: INSTANCE_KEY, action })
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          const text = await response.text();
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      showNotification(data.message || `${instanceName} ${action}ed!`, 'success');
      setIsRunning(newCheckedState);

      const icon = action === 'start' ? '✅' : '🛑';
      addActivity(icon, `${instanceName} ${action}ed`);

    } catch (e) {
      let errorMessage = e.message;
      if (e.message === 'Failed to fetch' || e.name === 'TypeError') {
        errorMessage = 'Network error: Could not connect to the server. Please check your connection or contact support.';
      }
      showNotification(`Error: ${errorMessage}`, 'error');
      console.error('Action failed:', e);
      // Revert toggle on failure
      setIsRunning(!newCheckedState);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Effects ---

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    const intervalId = setInterval(fetchStatus, 30000);
    return () => clearInterval(intervalId);
  }, [fetchStatus]);

  // --- Render ---
  return (
    <>
      <div className="top-bar">
        <div className="logo">
          <img src="/logo.webp" alt="Converge AI Logo" />
        </div>
        <h1 className="top-bar-title">Converge AI ECS Management Dashboard</h1>
      </div>

      <div className="container">
        <div className="header">
          <h1 className="header-title">Cluster Control</h1>
          <p className="header-subtitle">Manage Converge AI project ECS clusters with one click</p>
          <span className="header-badge">Powered by DevOps Team</span>
        </div>

        <div className="control-panel">
          <h2 className="panel-title">🖥️ ECS Cluster Controls</h2>

          <div className="env-card">
            <div className="env-header">
              <div className="env-info">
                <span className={`env-status-indicator ${isRunning ? 'active' : 'inactive'}`}></span>
                <div className="env-details">
                  <h3>{instanceName}</h3>
                  <p>{clusterName}</p>
                </div>
              </div>
              <div className={`env-status-badge ${isRunning ? 'active' : 'inactive'}`}>
                {isRunning ? 'Running' : 'Stopped'}
              </div>
            </div>
            <div className="env-controls">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  aria-label={`Toggle ${instanceName}`}
                  checked={isRunning}
                  disabled={isLoading}
                  onChange={(e) => handleToggle(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Notification Area */}
          {notification.show && (
            <div id="result" className={`show ${notification.type}`}>
              {notification.message}
            </div>
          )}

          <div className="activity-section">
            <div className="activity-title">📋 Recent Activity</div>
            <div className="activity-list">
              {activity.length === 0 ? (
                <div className="empty-activity">No activity yet. Start or stop an instance to see logs here.</div>
              ) : (
                activity.map((item, index) => (
                  <div className="activity-item" key={index}>
                    <span className="activity-icon">{item.icon}</span>
                    <span className="activity-text">{item.text}</span>
                    <span className="activity-time">{item.time}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="footer">
          Built by DevOps Team • Please contact <a href="mailto:devops@yourcompany.com">DevOps Team</a> for support
        </div>
      </div>
    </>
  );
}

export default App;
